import React, { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Settings,
  Building2,
  ArrowLeft,
  Moon,
  Sun,
  Wrench,
  LayoutDashboard,
  Lock,
  LogOut
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ToolsArea = ({ onBack }) => {
  const [darkMode, setDarkMode] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: ''
  });

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  const allApplications = [
    {
      id: 'pdf-compressor',
      name: 'PDF Compressor',
      description: 'Compresie și optimizare fișiere PDF',
      icon: Settings,
      iconBg: 'bg-violet-100 dark:bg-violet-900/30',
      iconColor: 'text-violet-600 dark:text-violet-400',
      url: 'https://nxtstp.ro/pdf-compressor/'
    },
    {
      id: 'management-hub',
      name: 'Management Hub',
      description: 'Centru de comandă și control operațiuni',
      icon: LayoutDashboard,
      iconBg: 'bg-rose-100 dark:bg-rose-900/30',
      iconColor: 'text-rose-600 dark:text-rose-400',
      url: 'https://nxtstp.ro/management-hub/'
    },
    {
      id: 'organizator-santiere',
      name: 'Organizator Șantiere',
      description: 'Planificare și organizare activități șantiere',
      icon: Building2,
      iconBg: 'bg-orange-100 dark:bg-orange-900/30',
      iconColor: 'text-orange-600 dark:text-orange-400',
      url: 'https://nxtstp.ro/organizator-santiere/'
    }
  ];

  useEffect(() => {
    checkExistingAuth();
  }, []);

  const checkExistingAuth = async () => {
    const token = localStorage.getItem('tools_token');
    
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/verify-token`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('tools_token');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);

    try {
      const response = await axios.post(`${API}/login`, loginForm);
      const userData = response.data.user;
      
      // Check if user has any tools permissions
      const hasAnyAccess = 
        userData.role === 'supreme_admin' || 
        userData.role === 'admin' ||
        (userData.tools_permissions && userData.tools_permissions.length > 0);

      if (!hasAnyAccess) {
        toast.error('Nu ai acces la nicio aplicație din Tools Area. Contactează administratorul.');
        setLoginLoading(false);
        return;
      }

      localStorage.setItem('tools_token', response.data.access_token);
      setUser(userData);
      toast.success(`Bine ai venit, ${userData.full_name}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Autentificare eșuată');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('tools_token');
    setUser(null);
    setLoginForm({ username: '', password: '' });
    toast.info('Te-ai deconectat');
  };

  // Track opened windows to avoid duplicate tabs
  const windowRefs = useRef({});

  const handleAppClick = (app) => {
    const existing = windowRefs.current[app.id];

    // If window is still open, just focus it
    if (existing && !existing.closed) {
      existing.focus();
      return;
    }

    // Open new window and store reference
    windowRefs.current[app.id] = window.open(app.url, `app-${app.id}`);
  };

  // Filter applications based on user permissions
  const getVisibleApplications = () => {
    if (!user) return [];
    
    // Supreme admin and admin see all apps
    if (user.role === 'supreme_admin' || user.role === 'admin') {
      return allApplications;
    }
    
    // Regular users see only permitted apps
    return allApplications.filter(app => 
      user.tools_permissions?.includes(app.id)
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  // If user is logged in, show their permitted apps
  if (user) {
    const visibleApps = getVisibleApplications();

    return (
      <div className="min-h-screen relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl" />
          <div className="absolute top-1/2 -left-40 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 right-1/3 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl" />
        </div>

        {/* Header */}
        <header className="relative z-10 px-6 py-8">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="icon"
                onClick={onBack}
                className="rounded-xl border-border hover:bg-accent-light/50 hover:border-accent transition-smooth"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-elegant-md">
                <Wrench className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-foreground">Tools Area</h1>
                <p className="text-xs text-muted-foreground">Bine ai venit, {user.full_name}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={toggleDarkMode}
                className="rounded-xl border-border hover:bg-accent-light/50 hover:border-accent transition-smooth"
              >
                {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={handleLogout}
                className="rounded-xl border-border hover:bg-destructive hover:text-destructive-foreground transition-smooth"
                title="Deconectare"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="relative z-10 px-6 pb-12">
          <div className="max-w-7xl mx-auto">
            {/* Welcome Section */}
            <div className="text-center mb-12 animate-fade-in">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-4">
                Aplicațiile Tale
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
                {visibleApps.length > 0 
                  ? 'Selectează o aplicație pentru a continua'
                  : 'Nu ai acces la nicio aplicație. Contactează administratorul.'
                }
              </p>
            </div>

            {/* Applications Grid */}
            {visibleApps.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visibleApps.map((app) => {
                  const IconComponent = app.icon;
                  return (
                    <Card
                      key={app.id}
                      className="group relative bg-card hover:bg-accent/5 border-border hover:border-amber-500/50 rounded-2xl p-6 cursor-pointer transition-all duration-300 hover:shadow-elegant-lg hover:-translate-y-1"
                      onClick={() => handleAppClick(app)}
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-amber-500/0 via-amber-500/0 to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl" />
                      
                      <div className="relative z-10">
                        <div className={`w-14 h-14 rounded-xl ${app.iconBg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                          <IconComponent className={`w-7 h-7 ${app.iconColor}`} />
                        </div>
                        
                        <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
                          {app.name}
                        </h3>
                        
                        <p className="text-sm text-muted-foreground">
                          {app.description}
                        </p>
                      </div>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 rounded-2xl p-8 text-center max-w-md mx-auto">
                <Lock className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Fără acces</h3>
                <p className="text-sm text-muted-foreground">
                  Contactează administratorul pentru a primi acces la aplicații.
                </p>
              </Card>
            )}
          </div>
        </main>
      </div>
    );
  }

  // Login form
  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 right-1/3 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 px-6 py-8">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={onBack}
              className="rounded-xl border-border hover:bg-accent-light/50 hover:border-accent transition-smooth"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-elegant-md">
              <Wrench className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-foreground">Tools Area</h1>
              <p className="text-xs text-muted-foreground">Autentificare necesară</p>
            </div>
          </div>
          
          <Button
            variant="outline"
            size="icon"
            onClick={toggleDarkMode}
            className="rounded-xl border-border hover:bg-accent-light/50 hover:border-accent transition-smooth"
          >
            {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>
      </header>

      {/* Main Content - Login Form */}
      <main className="relative z-10 px-6 pb-12">
        <div className="max-w-md mx-auto">
          <Card className="bg-card border-border rounded-2xl p-8 shadow-elegant-lg">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Lock className="w-8 h-8 text-amber-600 dark:text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Autentificare
              </h2>
              <p className="text-sm text-muted-foreground">
                Introdu credențialele pentru a accesa aplicațiile tale
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Nume utilizator</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Introdu numele de utilizator"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                  required
                  className="rounded-xl"
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Parolă</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  required
                  className="rounded-xl"
                />
              </div>

              <Button
                type="submit"
                className="w-full rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
                disabled={loginLoading}
              >
                {loginLoading ? 'Se autentifică...' : 'Autentificare'}
              </Button>
            </form>

            <p className="mt-4 text-xs text-center text-muted-foreground">
              Contul și permisiunile sunt gestionate de administrator
            </p>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default ToolsArea;
